import type { TableView } from "owa-mail-list-store";
import { MailRowDataPropertyGetter } from "owa-mail-list-store";
import { shouldTableSortByRenewTime, isDraftsMeaningfulSaveTimeEnabled, } from "owa-mail-triage-common";
import { getMailboxInfo } from "owa-mail-mailboxinfo";
import folderIdToName from "owa-session-store/lib/utils/folderIdToName";

/**
 * Whether the table should be ordered by the Drafts meaningful-save-time sort key
 * (LastMeaningfulDraftSaveTime primary, LastModifiedTime tie-breaker). True only for the
 * Drafts folder when the tri-draftsMeaningfulSaveTime flight is on for the account; with the
 * flight off callers keep the pre-feature LastModifiedTime-only ordering.
 */
export function shouldUseDraftsMeaningfulSaveTimeSort(tableView: TableView): boolean {
    return (folderIdToName(tableView.tableQuery.folderId) === "drafts" &&
        isDraftsMeaningfulSaveTimeEnabled(getMailboxInfo(tableView)));
}

/**
 * Compares two rows on the Drafts meaningful-save-time sort key, mirroring the FindItem
 * SortOrder we send: LastMeaningfulDraftSaveTime primary with LastModifiedTime as tie-breaker.
 *
 * A draft the service never stamped has no primary value and is ranked lower than every stamped
 * draft regardless of its LastModifiedTime, so a bot bump to LastModifiedTime cannot float it
 * above genuinely saved drafts. A draft whose stamp cannot be parsed is treated as unstamped.
 * Within a group, rows are ordered by LastModifiedTime.
 *
 * The caller applies the sort direction, so the unstamped group lands last in the descending
 * order Drafts ships with. Ascending Drafts sort is not used today and the server's null
 * placement for it has not been verified, so revisit this ranking before enabling one.
 *
 * @returns reference minus target, so < 0 when the reference row sorts earlier.
 */
function compareDraftsSortKey(targetLastMeaningfulDraftSaveTime: string | undefined, targetLastModifiedTimeStamp: string, referenceLastMeaningfulDraftSaveTime: string | undefined, referenceLastModifiedTimeStamp: string): number {
    // An unparseable LastMeaningfulDraftSaveTime cannot be trusted as a primary sort key, so treat
    // it the same as an absent one and let the row fall into the null-primary group. Comparing it
    // directly would yield NaN, which satisfies none of the caller's ordering checks and would
    // leave the row unplaced.
    const referenceMeaningfulSaveTime = referenceLastMeaningfulDraftSaveTime
        ? new Date(referenceLastMeaningfulDraftSaveTime).getTime()
        : NaN;
    const targetMeaningfulSaveTime = targetLastMeaningfulDraftSaveTime
        ? new Date(targetLastMeaningfulDraftSaveTime).getTime()
        : NaN;
    const referenceIsStamped = !isNaN(referenceMeaningfulSaveTime);
    const targetIsStamped = !isNaN(targetMeaningfulSaveTime);
    
    if (referenceIsStamped && targetIsStamped) {
        const diffMeaningfulSaveTime = referenceMeaningfulSaveTime - targetMeaningfulSaveTime;
        
        if (diffMeaningfulSaveTime !== 0) {
            return diffMeaningfulSaveTime;
        }
    }
    else if (referenceIsStamped) {
        return 1;
    }
    else if (targetIsStamped) {
        return -1;
    }
    
    const referenceLastModified = new Date(referenceLastModifiedTimeStamp).getTime();
    const targetLastModified = new Date(targetLastModifiedTimeStamp).getTime();
    
    // An absent or unparseable LastModifiedTime yields NaN, which would make every comparison
    // against this row false and let the caller drop it at the end of the list. Treat the rows as
    // tied instead so the row keeps its server-assigned position.
    if (isNaN(referenceLastModified) || isNaN(targetLastModified)) {
        return 0;
    }
    
    return referenceLastModified - targetLastModified;
}

/**
 * Compare the date time of two rows
 * @param targetLastDeliveryOrRenewTimeStamp the lastDeliveryOrRenewTimeStamp of the conversation to insert
 * @param targetLastDeliveryTimeStamp the lastDeliveryTimeStamp of the conversation to insert, or the
 * lastModifiedTime of the item to insert when the table uses the Drafts sort key
 * @param rowKeyOfReferenceRow the conversation rowKey of the reference row
 * @param tableView the table view
 * @param targetLastMeaningfulDraftSaveTime the lastMeaningfulDraftSaveTime of the item to insert,
 * undefined when the service never stamped it. Only used when the table uses the Drafts sort key.
 * @returns
 * 1) < 0 if reference row is earlier than target row
 * 2) 0 if have the same time
 * 3) > 0 if reference row is later than target row
 */
export default function compareRowDateTime(targetLastDeliveryOrRenewTimeStamp: string, targetLastDeliveryTimeStamp: string, rowKeyOfReferenceRow: string, tableView: TableView, targetLastMeaningfulDraftSaveTime?: string): number {
    const isDraftsFolder = folderIdToName(tableView.tableQuery.folderId) === "drafts";
    // For Drafts, key the reference row the same way the target row is keyed in onAddOrUpdateRow.
    // A mismatched key here would make the comparator asymmetric and misplace or drop rows during
    // notification repositioning.
    if (shouldUseDraftsMeaningfulSaveTimeSort(tableView)) {
        return compareDraftsSortKey(targetLastMeaningfulDraftSaveTime, targetLastDeliveryTimeStamp, MailRowDataPropertyGetter.getLastMeaningfulDraftSaveTimeStamp(rowKeyOfReferenceRow, tableView), MailRowDataPropertyGetter.getLastModifiedTimeStamp(rowKeyOfReferenceRow, tableView));
    }
    
    const lastDeliveryTimeStampOfReferenceRow = isDraftsFolder
        ? MailRowDataPropertyGetter.getLastModifiedTimeStamp(rowKeyOfReferenceRow, tableView)
        : MailRowDataPropertyGetter.getLastDeliveryTimeStamp(rowKeyOfReferenceRow, tableView);
    
    const diffDeliveryUTCTime = new Date(lastDeliveryTimeStampOfReferenceRow).getTime() -
        new Date(targetLastDeliveryTimeStamp).getTime();
    
    if (shouldTableSortByRenewTime(tableView.tableQuery)) {
        // If table supports pin, use renew time as primary sort, and delivery time as secondary sort
        const diffRenewTimeInUTC = new Date(MailRowDataPropertyGetter.getLastDeliveryOrRenewTimeStamp(rowKeyOfReferenceRow, tableView)).getTime() - new Date(targetLastDeliveryOrRenewTimeStamp).getTime();
        return diffRenewTimeInUTC || diffDeliveryUTCTime;
    }
    
    return diffDeliveryUTCTime;
}
